import { type ComponentType, useEffect } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useCertainCleanup } from '../../hooks/useCertainCleanup';
import { useStores } from '../../stores/useStores';
import { WizardStore } from '../../stores/wizard/WizardStore';

import { StepWrapper } from './StepWrapper';
import type { LocalWizardState, WizardChildProps } from './types';
import styles from './wizard.module.scss';
import { WizardStoreContext } from './WizardStoreContext';

export const Wizard = observer(function Wizard(props: {
  steps: ComponentType<WizardChildProps>[];
  classNameWrapper?: string;
  classNameSteps?: string;
  wrapperStyle?: React.CSSProperties;
  localWizardState?: LocalWizardState | WizardStore;
}) {
  const { wizardStore, loyaltyProgramStore } = useStores();

  const wizardState = props.localWizardState || wizardStore;
  const { step, height, setHeight, nextStep, prevStep, forceStep } = wizardState;

  useEffect(() => {
    if (!props.localWizardState && wizardStore.initialStep != null) {
      wizardStore.forceStep(wizardStore.initialStep);
    }
  }, [wizardStore, props.localWizardState]);

  useCertainCleanup(
    // reset step to zero on unmount (only for global wizard store)
    () => {
      if (!props.localWizardState) {
        wizardStore.forceStep(0);
        wizardStore.initialStep = null;
      }
    },
    () => undefined,
    [wizardStore, props.localWizardState],
  );

  const transform = loyaltyProgramStore.isLoyaltyProgramDialogOpenWithTutorial ? 'none' : `translateX(-${step * 100}%)`;

  const wizardStoreForContext = props.localWizardState instanceof WizardStore ? props.localWizardState : wizardStore;

  return (
    <WizardStoreContext.Provider value={wizardStoreForContext}>
      <div className={classNames(styles.wizardWrapper, props.classNameWrapper)} style={props.wrapperStyle}>
        <div className={classNames(styles.wizardSteps, props.classNameSteps)} style={{ transform, height }}>
          {props.steps.map((Component, idx) => {
            const isCurrent = step === idx;
            return (
              <StepWrapper key={idx} isCurrent={isCurrent} setHeight={setHeight}>
                <Component forceStep={forceStep} isCurrent={isCurrent} nextStep={nextStep} prevStep={prevStep} />
              </StepWrapper>
            );
          })}
        </div>
      </div>
    </WizardStoreContext.Provider>
  );
});
